import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Database,
  Network,
  Code2,
  Box,
  Eye,
  Edit,
  MoreHorizontal,
  ShieldCheck,
  Lock,
  Settings,
  Check,
  Info,
  ArrowRight,
  Clock,
  ExternalLink,
} from 'lucide-react';

const TAB_ROUTES = {
  'overview': '/blockchain-anchoring',
  'anchored-records': '/blockchain-anchoring/anchored-records',
  'ipfs-files': '/blockchain-anchoring/ipfs-files',
  'verifications': '/blockchain-anchoring/verifications',
  'nodes': '/blockchain-anchoring/nodes',
  'smart-contracts': '/blockchain-anchoring/smart-contracts',
  'settings': '/blockchain-anchoring/settings',
};


const colors = ['#7c3aed', '#2563eb', '#22c55e', '#f97316', '#cbd5e1'];

const MODAL_TITLES = {
  1: 'Anchor New Record',
  2: 'Security & Signing - Anchor New Record',
  3: 'Review & Anchor',
};

const MODAL_SUBTITLES = {
  1: 'Create a new immutable record by generating hash and anchoring it to the blockchain/IPFS network.',
  2: 'Secure your data with post-quantum cryptography and trusted time stamping.',
  3: 'Review configuration and confirm blockchain anchoring.',
};

const TAB_ACTIVE_CLASS = 'border-b-2 border-blue-600 text-blue-600';
const TAB_INACTIVE_CLASS = 'text-gray-900 hover:text-gray-900';

const getTabClass = (isActive) =>
  `whitespace-nowrap pb-3 pt-1 text-xs font-medium transition ${
    isActive ? TAB_ACTIVE_CLASS : TAB_INACTIVE_CLASS
  }`;

const getModalClass = (modalStep) => {
  const size =
    modalStep === 3
      ? 'w-full h-full rounded-none'
      : 'rounded-xl max-h-[90vh] w-full max-w-4xl mx-4';
  return `relative overflow-y-auto bg-white shadow-2xl ${size}`;
};

const getSidebarWidth = () =>
  document.querySelector('.sidebar-aside')?.offsetWidth ?? 200;

const MetricCard = ({
  title,
  value,
  change,
  icon: Icon,
  iconColor,
  changeColor = 'text-green-600',
}) => (
  <div className="min-w-0 rounded-md border border-gray-200 bg-white px-3 py-2 shadow-sm">
    <div className="flex items-center gap-2">
      <div
        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-gray-50 ${iconColor}`}
      >
        <Icon className="h-7 w-7" />
      </div>

      <div className="min-w-0">
        <p className="truncate text-[11px] font-medium leading-tight text-gray-500">
          {title}
        </p>
        <p className="text-xl font-bold leading-tight text-gray-900">
          {value}
        </p>
        <p className={`text-[9px] leading-tight ${changeColor}`}>{change}</p>
      </div>
    </div>
  </div>
);

const DonutChart = ({ data, total, totalLabel = 'Total', height = 160 }) => (
  <div className="relative h-full w-full">
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={40}
          outerRadius={60}
          paddingAngle={2}
          startAngle={90}
          endAngle={-270}
          dataKey="value"
          stroke="none"
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${entry.name}`}
              fill={colors[index % colors.length]}
            />
          ))}
        </Pie>

        <Tooltip
          formatter={(value, name) => [
            typeof value === 'number' ? value.toLocaleString() : value,
            name,
          ]}
        />
      </PieChart>
    </ResponsiveContainer>

    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
      <span className="text-base font-bold leading-none text-gray-900">
        {total}
      </span>
      <span className="mt-0.5 text-[9px] text-gray-500">{totalLabel}</span>
    </div>
  </div>
);

const StepBadge = ({ step, label, modalStep }) => {
  const done = modalStep > step;
  const current = modalStep === step;
  return (
    <div
      className={`flex items-center gap-2 text-xs ${
        done || current ? 'text-indigo-600' : 'text-gray-500'
      }`}
    >
      <span
        className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${
          done || current
            ? 'bg-indigo-600 text-white'
            : 'border border-gray-300 bg-gray-100 text-gray-500'
        }`}
      >
        {done ? <Check size={14} strokeWidth={3} /> : step}
      </span>
      <span className={current ? 'font-bold' : 'font-medium'}>{label}</span>
    </div>
  );
};

const AnchorStep1 = ({ closeModal, setModalStep }) => (
    <div className="grid grid-cols-1 gap-4 px-6 pt-0 pb-0.5 md:grid-cols-2">
      <div>
        <h3 className="mb-4 text-sm font-bold text-gray-900">
          1. Record Details
        </h3>

        <label className="mb-1 block text-xs font-semibold text-gray-700">
          Record Type <span className="text-red-500">*</span>
        </label>

        <select className="mb-4 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500">
          <option>Document</option>
          <option>Artifact</option>
          <option>Certificate</option>
          <option>Transaction</option>
        </select>

        <label className="mb-1 block text-xs font-semibold text-gray-700">
          File Upload <span className="text-red-500">*</span>
        </label>

        <div className="mb-4 flex min-h-[140px] flex-col items-center justify-center rounded-md border border-gray-300 bg-gray-50 px-4 text-center">
          <svg
            className="mb-2 h-9 w-9 text-indigo-600"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 16V4m0 0L8 8m4-4 4 4M5 14v3a3 3 0 0 0 3 3h8a3 3 0 0 0 3-3v-3"
            />
          </svg>

          <p className="text-xs font-medium text-gray-700">
            Drag and drop file here
          </p>

          <p className="my-1 text-xs text-gray-500">or</p>

          <label className="cursor-pointer rounded-md border border-indigo-300 bg-white px-4 py-2 text-xs font-semibold text-indigo-600 transition hover:bg-indigo-50">
            Browse Files
            <input type="file" className="hidden" />
          </label>

          <p className="mt-2 text-[10px] text-gray-500">
            Supported formats: PDF, DOCX, TXT, XLSX, JSON, ZIP, etc.
          </p>

          <p className="text-[10px] text-gray-500">
            Max file size: 100 MB
          </p>
        </div>

        <label className="mb-1 block text-xs font-semibold text-gray-700">
          Description
        </label>

        <textarea
          rows={4}
          maxLength={500}
          placeholder="Enter a brief description of the record..."
          className="w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
        />

        <p className="text-right text-[10px] text-gray-400">0/500</p>
      </div>

      <div className="self-start border-l-0 border-gray-200 lg:border-l lg:pl-6">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900">
            Metadata{' '}
            <span className="font-normal text-gray-400">
              (Optional)
            </span>
          </h3>

          <span className="text-gray-400">⌃</span>
        </div>

        <label className="mb-1 block text-xs font-semibold text-gray-700">
          Document Name
        </label>

        <input
          type="text"
          placeholder="e.g. Agreement_2025.pdf"
          className="mb-4 w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
        />

        <label className="mb-1 block text-xs font-semibold text-gray-700">
          Category
        </label>

        <select className="mb-4 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-500 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500">
          <option>Select category</option>
          <option>Legal</option>
          <option>Finance</option>
          <option>Contract</option>
          <option>Compliance</option>
          <option>Other</option>
        </select>

        <label className="mb-1 block text-xs font-semibold text-gray-700">
          Tags
        </label>

        <input
          type="text"
          placeholder="e.g. legal, finance, contract (comma separated)"
          className="mb-4 w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
        />

        <label className="mb-1 block text-xs font-semibold text-gray-700">
          Reference ID
        </label>

        <input
          type="text"
          placeholder="e.g. PO-12345, Ticket-6789"
          className="mb-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
        />

        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900">
            Advanced Options
          </h3>

          <span className="text-gray-400">⌃</span>
        </div>

        <div className="space-y-3">
          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input
              type="checkbox"
              defaultChecked
              className="h-4 w-4 accent-indigo-600"
            />
            Store file on IPFS (distributed storage)
          </label>

          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input
              type="checkbox"
              defaultChecked
              className="h-4 w-4 accent-indigo-600"
            />
            Include certificate chain
          </label>

          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 accent-indigo-600"
            />
            Use HSM signing (if available)
          </label>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            onClick={closeModal}
            className="rounded-md border border-gray-300 px-4 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
          >
            Cancel
          </button>

          <button
            onClick={() => setModalStep(2)}
            className="rounded-md bg-indigo-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-indigo-700"
          >
            Next: Security &amp; Signing →
          </button>
        </div>
      </div>
    </div>
);

const AnchorStep2 = ({ form, setModalStep }) => {
  const {
    selectedProfile, setSelectedProfile,
    signingAlgo, setSigningAlgo,
    keySource, setKeySource,
    certificate, setCertificate,
    timestampAuth, setTimestampAuth,
    timestampAlgo, setTimestampAlgo,
    includeBlockchain, setIncludeBlockchain,
    encryptRecord, setEncryptRecord,
    enableAudit, setEnableAudit,
    storeIpfs, setStoreIpfs,
  } = form;

  return (
      <div className="px-6 pt-0 pb-0.5 font-sans">
        {/* Section 1: Select Security Profile */}
        <div className="mb-8">
          <h3 className="font-bold text-gray-900 text-sm mb-1">1. Select Security Profile</h3>
          <p className="text-gray-500 text-xs mb-4">Choose the appropriate security level for your record.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Card 1: Standard Security */}
            <div 
              onClick={() => setSelectedProfile('standard')}
              className={`rounded-xl p-4 relative cursor-pointer transition-all ${
                selectedProfile === 'standard' 
                  ? 'border-2 border-indigo-500 bg-indigo-50/40 shadow-sm' 
                  : 'border border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2 text-indigo-700 font-bold text-sm">
                  <ShieldCheck size={18} className="text-indigo-600" /> Standard Security
                </div>
                <span className="bg-indigo-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full">Default</span>
              </div>
              <p className="text-[13px] text-gray-600 leading-snug">
                PQC signature + Timestamp.<br/>
                <span className="text-gray-500 text-xs">(Recommended for most use cases)</span>
              </p>
            </div>

            {/* Card 2: High Security */}
            <div 
              onClick={() => setSelectedProfile('high')}
              className={`rounded-xl p-4 cursor-pointer transition-all ${
                selectedProfile === 'high' 
                  ? 'border-2 border-indigo-500 bg-indigo-50/40 shadow-sm' 
                  : 'border border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 text-gray-800 font-bold text-sm mb-2">
                <Lock size={18} className="text-indigo-600" /> High Security
              </div>
              <p className="text-[13px] text-gray-600 leading-snug">
                PQC signature + HSM + Timestamp.<br/>
                <span className="text-gray-500 text-xs">(For sensitive / critical data)</span>
              </p>
            </div>

            {/* Card 3: Enterprise / Compliance */}
            <div 
              onClick={() => setSelectedProfile('enterprise')}
              className={`rounded-xl p-4 cursor-pointer transition-all ${
                selectedProfile === 'enterprise' 
                  ? 'border-2 border-indigo-500 bg-indigo-50/40 shadow-sm' 
                  : 'border border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 text-gray-800 font-bold text-sm mb-2">
                <Settings size={18} className="text-indigo-600" /> Enterprise / Compliance
              </div>
              <p className="text-[13px] text-gray-600 leading-snug">
                PQC signature + HSM + Blockchain.<br/>
                <span className="text-gray-500 text-xs">(For regulatory / legal requirements)</span>
              </p>
            </div>
          </div>
        </div>

        {/* Section 2: Signing Configuration */}
        <div className="mb-8">
          <h3 className="font-bold text-gray-900 text-sm mb-1">2. Signing Configuration</h3>
          <p className="text-gray-500 text-xs mb-4">Configure the signing algorithm and key source.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">
                  Signing Algorithm <span className="text-red-500">*</span>
                </label>
                <select 
                  value={signingAlgo}
                  onChange={(e) => setSigningAlgo(e.target.value)}
                  className="w-full border border-gray-200 text-gray-700 rounded-lg px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 shadow-sm"
                >
                  <option>Dilithium 3 (PQC)</option>
                  <option>Falcon 512 (PQC)</option>
                  <option>SPHINCS+ (PQC)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">
                  Key Source <span className="text-red-500">*</span>
                </label>
                <select 
                  value={keySource}
                  onChange={(e) => setKeySource(e.target.value)}
                  className="w-full border border-gray-200 text-gray-700 rounded-lg px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 shadow-sm"
                >
                  <option>HSM (Hardware Security Module)</option>
                  <option>KMS (Key Management Service)</option>
                  <option>Local Secure Vault</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">
                  Certificate <span className="text-red-500">*</span>
                </label>
                <select 
                  value={certificate}
                  onChange={(e) => setCertificate(e.target.value)}
                  className="w-full border border-gray-200 text-gray-700 rounded-lg px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 shadow-sm"
                >
                  <option>t-PQSign TSA 01 (Active)</option>
                  <option>t-PQSign TSA 02 (Standby)</option>
                </select>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">
                  Timestamp Authority <span className="text-red-500">*</span>
                </label>
                <select 
                  value={timestampAuth}
                  onChange={(e) => setTimestampAuth(e.target.value)}
                  className="w-full border border-gray-200 text-gray-700 rounded-lg px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 shadow-sm"
                >
                  <option>GlobalSign TSA</option>
                  <option>DigiCert TSA</option>
                  <option>Sectigo TSA</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">
                  Timestamp Algorithm <span className="text-red-500">*</span>
                </label>
                <select 
                  value={timestampAlgo}
                  onChange={(e) => setTimestampAlgo(e.target.value)}
                  className="w-full border border-gray-200 text-gray-700 rounded-lg px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 shadow-sm"
                >
                  <option>SHA-256</option>
                  <option>SHA-384</option>
                  <option>SHA-512</option>
                </select>
              </div>
              
              {/* Toggle Switch */}
              <div className="pt-2">
                <div className="flex items-center gap-2 mb-1">
                  <button 
                    type="button" 
                    onClick={() => setIncludeBlockchain(!includeBlockchain)}
                    className={`w-9 h-5 rounded-full flex items-center p-0.5 cursor-pointer transition-colors ${
                      includeBlockchain ? 'bg-indigo-600' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${
                      includeBlockchain ? 'translate-x-4' : 'translate-x-0'
                    }`}></div>
                  </button>
                  <span className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
                    Include Blockchain Anchor 
                    <Info size={14} className="text-gray-400" />
                  </span>
                </div>
                <p className="text-xs text-gray-500 ml-11">Store hash on blockchain for immutability</p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: Additional Security Options */}
        <div>
          <h3 className="font-bold text-gray-900 text-sm mb-4">3. Additional Security Options</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            {/* Checkboxes */}
            <div className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer group">
                <input 
                  type="checkbox" 
                  checked={encryptRecord}
                  onChange={(e) => setEncryptRecord(e.target.checked)}
                  className="hidden" 
                />
                <div className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${
                  encryptRecord ? 'bg-indigo-600 text-white' : 'border border-gray-300 bg-white'
                }`}>
                  {encryptRecord && <Check size={14} strokeWidth={3} />}
                </div>
                <span className="text-sm text-gray-800 font-medium group-hover:text-indigo-700">Encrypt the record (AES-256)</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer group">
                <input 
                  type="checkbox" 
                  checked={enableAudit}
                  onChange={(e) => setEnableAudit(e.target.checked)}
                  className="hidden" 
                />
                <div className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${
                  enableAudit ? 'bg-indigo-600 text-white' : 'border border-gray-300 bg-white'
                }`}>
                  {enableAudit && <Check size={14} strokeWidth={3} />}
                </div>
                <span className="text-sm text-gray-800 font-medium group-hover:text-indigo-700">Enable audit logging</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer group">
                <input 
                  type="checkbox" 
                  checked={storeIpfs}
                  onChange={(e) => setStoreIpfs(e.target.checked)}
                  className="hidden" 
                />
                <div className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${
                  storeIpfs ? 'bg-indigo-600 text-white' : 'border border-gray-300 bg-white'
                }`}>
                  {storeIpfs && <Check size={14} strokeWidth={3} />}
                </div>
                <span className="text-sm text-gray-800 font-medium group-hover:text-indigo-700">Store copy in IPFS</span>
              </label>
            </div>

            {/* Dynamic Security Summary Box */}
            <div className="bg-indigo-50/60 rounded-lg p-3 border border-indigo-100/50">
              <div className="flex items-center gap-1.5 text-indigo-900 font-bold text-xs mb-2">
                <Lock size={13} className="text-indigo-600" fill="currentColor" /> Security Summary
              </div>
              <div className="text-xs space-y-1.5">
                <div className="grid grid-cols-[90px_1fr]">
                  <span className="text-gray-500">Algorithm</span>
                  <span className="text-gray-800 font-medium">: {signingAlgo}</span>
                </div>
                <div className="grid grid-cols-[90px_1fr]">
                  <span className="text-gray-500">Timestamp</span>
                  <span className="text-gray-800 font-medium">: {timestampAuth} ({timestampAlgo})</span>
                </div>
                <div className="grid grid-cols-[90px_1fr]">
                  <span className="text-gray-500">Key Source</span>
                  <span className="text-gray-800 font-medium">: {keySource.split(' ')[0]}</span>
                </div>
                <div className="grid grid-cols-[90px_1fr]">
                  <span className="text-gray-500">Blockchain</span>
                  <span className="text-gray-800 font-medium">: {includeBlockchain ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Navigation Buttons */}
        <div className="mt-8 pt-5 border-t border-gray-100 flex justify-between items-center">
          <button 
            type="button"
            onClick={() => setModalStep(1)}
            className="px-6 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg text-sm font-semibold text-gray-600 transition-colors shadow-sm"
          >
            Back
          </button>
          <button 
            type="button"
            onClick={() => setModalStep(3)}
            className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors shadow-sm shadow-indigo-200"
          >
            Next: Review &amp; Anchor <ArrowRight size={16} />
          </button>
        </div>
      </div>
  );
};

const AnchorStep3 = ({
  signingAlgo,
  keySource,
  certificate,
  timestampAuth,
  timestampAlgo,
  includeBlockchain,
  confirmCheck,
  setConfirmCheck,
  setModalStep,
  closeModal,
}) => (
    <div className="p-6 font-sans bg-gray-50/20">
      {/* Title Section */}
      <div className="mb-6">
        <h2 className="text-lg font-bold text-gray-900">Review Your Record</h2>
        <p className="text-xs text-gray-500 mt-1">
          Please review the details below before anchoring. Once confirmed, the record will be cryptographically signed and stored on the blockchain/IPFS with a timestamp.
        </p>
      </div>

      {/* Main 2-Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* Left Main Content Column */}
        <div className="lg:col-span-7 space-y-5">
          
          {/* Card 1: Record Summary */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between pb-3 mb-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-900 text-sm">1. Record Summary</h3>
              <button 
                onClick={() => setModalStep(1)} 
                className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
              >
                <Edit size={13} /> Edit
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6 text-xs">
              <div className="grid grid-cols-[110px_1fr]">
                <span className="text-gray-500">Document Name</span>
                <span className="font-medium text-gray-900">: Agreement_2025.pdf</span>
              </div>
              <div className="grid grid-cols-[110px_1fr]">
                <span className="text-gray-500">File Name</span>
                <span className="font-medium text-gray-900">: Agreement_2025.pdf</span>
              </div>

              <div className="grid grid-cols-[110px_1fr]">
                <span className="text-gray-500">Category</span>
                <span className="font-medium text-gray-900">: Legal</span>
              </div>
              <div className="grid grid-cols-[110px_1fr]">
                <span className="text-gray-500">File Size</span>
                <span className="font-medium text-gray-900">: 2.45 MB</span>
              </div>

              <div className="grid grid-cols-[110px_1fr]">
                <span className="text-gray-500">Tags</span>
                <span className="font-medium text-gray-900">: finance, contract, agreement</span>
              </div>
              <div className="grid grid-cols-[110px_1fr]">
                <span className="text-gray-500">Document Type</span>
                <span className="font-medium text-gray-900">: PDF</span>
              </div>

              <div className="grid grid-cols-[110px_1fr]">
                <span className="text-gray-500">Reference ID</span>
                <span className="font-medium text-gray-900">: PO-12345-Ticket-6789</span>
              </div>
              <div className="grid grid-cols-[110px_1fr]">
                <span className="text-gray-500">Hash (SHA-256)</span>
                <span className="font-mono text-indigo-600 truncate">: a3f2ce65d6a7e9f1f2c8a6d5edf2ab9ac4d56df7a8b9e0cfdd213</span>
              </div>

              <div className="grid grid-cols-[110px_1fr]">
                <span className="text-gray-500">Description</span>
                <span className="text-gray-800 leading-snug">: Service agreement between Intrust and ABC Corp for cybersecurity services.</span>
              </div>
              <div className="grid grid-cols-[110px_1fr]">
                <span className="text-gray-500">Uploaded By</span>
                <span className="font-medium text-gray-900">: Demo Admin</span>
              </div>

              <div></div>
              <div className="grid grid-cols-[110px_1fr]">
                <span className="text-gray-500">Uploaded On</span>
                <span className="text-gray-800">: May 31, 2025 10:30 AM (UTC)</span>
              </div>
            </div>
          </div>

          {/* Card 2: Security & Signing Configuration */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between pb-3 mb-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-900 text-sm">2. Security &amp; Signing Configuration</h3>
              <button 
                onClick={() => setModalStep(2)} 
                className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
              >
                <Edit size={13} /> Edit
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6 text-xs">
              <div className="grid grid-cols-[150px_1fr]">
                <span className="text-gray-500">Signing Algorithm</span>
                <span className="font-medium text-gray-900">: {signingAlgo}</span>
              </div>
              <div className="grid grid-cols-[150px_1fr]">
                <span className="text-gray-500">Key Source</span>
                <span className="font-medium text-gray-900">: {keySource}</span>
              </div>

              <div className="grid grid-cols-[150px_1fr]">
                <span className="text-gray-500">Certificate</span>
                <span className="font-medium text-gray-900">: {certificate}</span>
              </div>
              <div className="grid grid-cols-[150px_1fr]">
                <span className="text-gray-500">Timestamp Authority</span>
                <span className="font-medium text-gray-900">: {timestampAuth}</span>
              </div>

              <div className="grid grid-cols-[150px_1fr]">
                <span className="text-gray-500">Timestamp Algorithm</span>
                <span className="font-medium text-gray-900">: {timestampAlgo}</span>
              </div>
              <div className="grid grid-cols-[150px_1fr]">
                <span className="text-gray-500">Timestamp Algorithm</span>
                <span className="font-medium text-gray-900">: {timestampAlgo}</span>
              </div>

              <div className="grid grid-cols-[150px_1fr]">
                <span className="text-gray-500">Blockchain Anchor</span>
                <span className="font-medium text-gray-900">: IPFS (Distributed Storage)</span>
              </div>
              <div className="grid grid-cols-[150px_1fr]">
                <span className="text-gray-500">Include Certificate Chain</span>
                <span className="font-medium text-gray-900">: Yes</span>
              </div>

              <div className="grid grid-cols-[150px_1fr]">
                <span className="text-gray-500">Store on Blockchain (Optional)</span>
                <span className="font-medium text-gray-900">: {includeBlockchain ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>

          {/* Card 3: Anchor Details (Preview) */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between pb-3 mb-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-900 text-sm">3. Anchor Details (Preview)</h3>
              <button 
                onClick={() => setModalStep(2)} 
                className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
              >
                <Edit size={13} /> Edit
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-[160px_1fr]">
                <span className="text-gray-500">Transaction ID (Preview)</span>
                <span className="font-mono text-indigo-600">: 0x7f3a9c4e2b6d...8e9f</span>
              </div>
              <div className="grid grid-cols-[160px_1fr]">
                <span className="text-gray-500">Timestamp (UTC)</span>
                <span className="font-medium text-gray-900">: May 31, 2025 10:32:45 AM</span>
              </div>
              <div className="grid grid-cols-[160px_1fr]">
                <span className="text-gray-500">Block Number (Preview)</span>
                <span className="font-medium text-gray-900">: 7,842,551</span>
              </div>
              <div className="grid grid-cols-[160px_1fr]">
                <span className="text-gray-500">IPFS CID (Preview)</span>
                <span className="font-mono text-indigo-600 break-all">: QmXn7e5g8JbW2n1r4zK9v6b7Q3h5T8a1pZL4m6NBo09pQr</span>
              </div>
            </div>
          </div>

          {/* Checkbox and Action Footer */}
          <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-700">
              <input 
                type="checkbox" 
                checked={confirmCheck}
                onChange={(e) => setConfirmCheck(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 accent-indigo-600"
              />
              <span>I confirm that the above details are correct and I want to anchor this record.</span>
            </label>

            <div className="flex items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={() => setModalStep(2)}
                className="rounded-lg border border-gray-200 bg-white px-6 py-2.5 text-xs font-semibold text-gray-600 shadow-sm transition-colors hover:bg-gray-50"
              >
                Back
              </button>

              <button
                type="button"
                onClick={closeModal}
                className="flex items-center gap-2 rounded-lg bg-indigo-600 px-6 py-2.5 text-xs font-semibold text-white shadow-sm shadow-indigo-200 transition-colors hover:bg-indigo-700"
              >
                <Lock size={14} /> Anchor Record
              </button>
            </div>
          </div>

        </div>

        {/* Right Sidebar Column */}
        <div className="lg:col-span-5 space-y-5">
          
          {/* Record Verification Status Box */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h3 className="font-bold text-gray-900 text-sm mb-3">Record Verification</h3>
            
            <div className="mb-4 flex items-start gap-3 rounded-lg bg-emerald-50 p-3 border border-emerald-100">
              <div className="rounded-full bg-emerald-500 text-white p-0.5 mt-0.5 shrink-0">
                <Check size={14} strokeWidth={3} />
              </div>
              <div>
                <h4 className="text-xs font-bold text-emerald-900">Valid and Ready to Anchor</h4>
                <p className="text-[11px] text-emerald-700 leading-snug mt-0.5">
                  All security checks passed. The record is ready to be anchored on blockchain/IPFS.
                </p>
              </div>
            </div>

            <div className="space-y-2.5 text-xs text-gray-700">
              <div className="flex items-center gap-2">
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 text-[10px]">
                  <Check size={11} strokeWidth={3} />
                </span>
                <span>File integrity verified (SHA-256)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 text-[10px]">
                  <Check size={11} strokeWidth={3} />
                </span>
                <span>Certificate chain valid</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 text-[10px]">
                  <Check size={11} strokeWidth={3} />
                </span>
                <span>TSA authority verified</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 text-[10px]">
                  <Check size={11} strokeWidth={3} />
                </span>
                <span>HSM signing successful</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 text-[10px]">
                  <Check size={11} strokeWidth={3} />
                </span>
                <span>Blockchain connection available</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 text-[10px]">
                  <Check size={11} strokeWidth={3} />
                </span>
                <span>IPFS storage ready</span>
              </div>
            </div>
          </div>

          {/* Blockchain / IPFS Details */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 font-bold text-gray-900 text-sm mb-4">
              <div className="p-1.5 rounded-md bg-indigo-50 text-indigo-600">
                <Database size={16} />
              </div>
              Blockchain / IPFS Details
            </div>

            <div className="space-y-2.5 text-xs">
              <div className="grid grid-cols-[120px_1fr]">
                <span className="text-gray-500">Network</span>
                <span className="font-semibold text-gray-900">: Ethereum (Public)</span>
              </div>
              <div className="grid grid-cols-[120px_1fr]">
                <span className="text-gray-500">Transaction ID</span>
                <span className="font-mono text-indigo-600 truncate">: 0x7f3a9c4e2b6d...8e9f</span>
              </div>
              <div className="grid grid-cols-[120px_1fr]">
                <span className="text-gray-500">Block Number</span>
                <span className="font-semibold text-gray-900">: 7,842,551</span>
              </div>
              <div className="grid grid-cols-[120px_1fr]">
                <span className="text-gray-500">Confirmation Status</span>
                <span className="font-medium text-amber-600">: Pending</span>
              </div>
              <div className="grid grid-cols-[120px_1fr]">
                <span className="text-gray-500">IPFS CID</span>
                <span className="font-mono text-indigo-600 truncate">: QmXn7e5g8JbW2n1r4zK9v6b7Q3h5T8a1pZL4m6NBo09pQr</span>
              </div>

              <div className="pt-2">
                <a href="#" onClick={(e) => e.preventDefault()} className="inline-flex items-center gap-1 text-indigo-600 font-semibold hover:underline text-xs">
                  View on Etherscan <ExternalLink size={13} />
                </a>
              </div>
            </div>
          </div>

          {/* Timestamp Information */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 font-bold text-gray-900 text-sm mb-4">
              <div className="p-1.5 rounded-md bg-purple-50 text-purple-600">
                <Clock size={16} />
              </div>
              Timestamp Information
            </div>

            <div className="space-y-2.5 text-xs">
              <div className="grid grid-cols-[110px_1fr]">
                <span className="text-gray-500">Timestamp</span>
                <span className="font-semibold text-gray-900">: May 31, 2025 10:32:45 AM (UTC)</span>
              </div>
              <div className="grid grid-cols-[110px_1fr]">
                <span className="text-gray-500">Authority</span>
                <span className="font-semibold text-gray-900">: GlobalSign TSA</span>
              </div>
              <div className="grid grid-cols-[110px_1fr]">
                <span className="text-gray-500">Algorithm</span>
                <span className="font-semibold text-gray-900">: SHA-256</span>
              </div>
              <div className="grid grid-cols-[110px_1fr]">
                <span className="text-gray-500">Certificate ID</span>
                <span className="font-semibold text-gray-900">: GSL-TSA-2025-001</span>
              </div>
            </div>
          </div>

          {/* Security Info Card */}
          <div className="rounded-xl bg-indigo-50/50 border border-indigo-100 p-4 text-xs text-indigo-900">
            <div className="flex gap-2.5 items-start">
              <ShieldCheck size={18} className="text-indigo-600 shrink-0 mt-0.5" />
              <div>
                <p className="leading-snug text-gray-700">
                  Once anchored, this record will be immutable and verifiable forever using the transaction ID and IPFS CID.
                </p>
                <a href="#" onClick={(e) => e.preventDefault()} className="inline-block mt-2 font-semibold text-indigo-600 hover:underline">
                  Learn more about blockchain anchoring &rarr;
                </a>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
);

export default function AnchorOverview() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showAnchorModal, setShowAnchorModal] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  
  // Interactive form states for Step 2
  const [selectedProfile, setSelectedProfile] = useState('standard');
  const [signingAlgo, setSigningAlgo] = useState('Dilithium 3 (PQC)');
  const [keySource, setKeySource] = useState('HSM (Hardware Security Module)');
  const [certificate, setCertificate] = useState('t-PQSign TSA 01 (Active)');
  const [timestampAuth, setTimestampAuth] = useState('GlobalSign TSA');
  const [timestampAlgo, setTimestampAlgo] = useState('SHA-256');
  const [includeBlockchain, setIncludeBlockchain] = useState(true);

  const [encryptRecord, setEncryptRecord] = useState(true);
  const [enableAudit, setEnableAudit] = useState(true);
  const [storeIpfs, setStoreIpfs] = useState(true);

  // Confirmation state for Step 3
  const [confirmCheck, setConfirmCheck] = useState(true);

  const handleTabClick = (tabKey) => {
    if (TAB_ROUTES[tabKey]) {
      navigate(TAB_ROUTES[tabKey]);
    } else {
      setActiveTab(tabKey);
    }
  };

  const form = {
    selectedProfile, setSelectedProfile,
    signingAlgo, setSigningAlgo,
    keySource, setKeySource,
    certificate, setCertificate,
    timestampAuth, setTimestampAuth,
    timestampAlgo, setTimestampAlgo,
    includeBlockchain, setIncludeBlockchain,
    encryptRecord, setEncryptRecord,
    enableAudit, setEnableAudit,
    storeIpfs, setStoreIpfs,
  };

  const closeModal = () => {
    setShowAnchorModal(false);
    setModalStep(1);
  };

  const openModal = () => {
    setModalStep(1);
    setShowAnchorModal(true);
  };

  const tabs = [
    'Overview',
    'Anchored Records',
    'IPFS Files',
    'Verifications',
    'Nodes',
    'Smart Contracts',
    'Settings',
  ];

  const trendData = [
    { date: 'May 1', value: 180 },
    { date: 'May 8', value: 300 },
    { date: 'May 15', value: 500 },
    { date: 'May 22', value: 520 },
    { date: 'May 29', value: 900 },
  ];

  const recordsTypeData = [
    { name: 'Document Signatures', value: 6248, percentage: 50.1 },
    { name: 'Artifact Signatures', value: 3124, percentage: 25.1 },
    { name: 'Certificates', value: 1856, percentage: 14.9 },
    { name: 'Transactions', value: 856, percentage: 6.9 },
    { name: 'Others', value: 374, percentage: 3.0 },
  ];

  const storageNetworkData = [
    { name: 'Ethereum', value: 5248, percentage: 42.1 },
    { name: 'Polygon', value: 3124, percentage: 25.1 },
    { name: 'Hyperledger Fabric', value: 2856, percentage: 22.9 },
    { name: 'BSC', value: 856, percentage: 6.9 },
    { name: 'Others', value: 374, percentage: 3.0 },
  ];

  const ipfsStorageData = [
    { name: 'Documents', value: 1.12, percentage: 45.7 },
    { name: 'Artifacts', value: 0.78, percentage: 31.8 },
    { name: 'Certificates', value: 0.32, percentage: 13.1 },
    { name: 'Others', value: 0.23, percentage: 9.4 },
  ];

  const recentRecords = [
    {
      id: 'AR-2025-05-31-001',
      type: 'Document Signature',
      identifier: 'Service_Agreement.pdf',
      ipfsHash: 'QmPTa1...2XU1c',
      networks: 'Ethereum',
      blocks: '17854652',
      timestamp: 'May 31, 2025 10:15:34 AM',
      status: 'Anchored',
      txHash: '0x43a7c1c2abc1...c1de3',
      confirmations: 128,
    },
    {
      id: 'AR-2025-05-31-002',
      type: 'Artifact Signature',
      identifier: 'Firmware_v2.1.bin',
      ipfsHash: 'QmN5dE...JmNp',
      networks: 'Polygon',
      blocks: '5284731',
      timestamp: 'May 31, 2025 10:22:10 AM',
      status: 'Anchored',
      txHash: '0x1a7c...52847',
      confirmations: 94,
    },
    {
      id: 'AR-2025-05-31-003',
      type: 'Certificate',
      identifier: 'John_Smith_Cert.pem',
      ipfsHash: 'QmP7L...n8cQ',
      networks: 'Hyperledger',
      blocks: 'tbd6d...e3af',
      timestamp: 'May 31, 2025 10:25:15 AM',
      status: 'Anchored',
      txHash: '0x88f2...e3af',
      confirmations: 256,
    },
    {
      id: 'AR-2025-05-31-004',
      type: 'Document Signature',
      identifier: 'NDA_Contract.pdf',
      ipfsHash: 'Qm6yP...2XJ7',
      networks: 'Ethereum',
      blocks: '17854690',
      timestamp: 'May 31, 2025 10:45:05 AM',
      status: 'Anchored',
      txHash: '0x3c2a...76f1',
      confirmations: 112,
    },
    {
      id: 'AR-2025-05-31-005',
      type: 'Artifact Signature',
      identifier: 'Mobile_App_release.apk',
      ipfsHash: 'Qm9kSP...a7mT3',
      networks: 'BSC',
      blocks: '0x9c4f...c1de3',
      timestamp: 'May 31, 2025 10:59:48 AM',
      status: 'Anchored',
      txHash: '0x9c4f...c1de3',
      confirmations: 45,
    },
  ];

  const networkNodes = [
    {
      nodeId: 'IPFS-NODE-01',
      network: 'IPFS',
      role: 'Storage',
      status: 'Online',
      lastSeen: 'May 31, 2025 10:32 AM',
      health: '100%',
    },
    {
      nodeId: 'IPFS-NODE-02',
      network: 'IPFS',
      role: 'Storage',
      status: 'Online',
      lastSeen: 'May 31, 2025 10:31 AM',
      health: '98%',
    },
    {
      nodeId: 'ETH-NODE-01',
      network: 'Ethereum',
      role: 'Full Node',
      status: 'Online',
      lastSeen: 'May 31, 2025 10:31 AM',
      health: '100%',
    },
    {
      nodeId: 'POLY-NODE-01',
      network: 'Polygon',
      role: 'Full Node',
      status: 'Online',
      lastSeen: 'May 31, 2025 10:31 AM',
      health: '98%',
    },
    {
      nodeId: 'FABRIC-PEER-01',
      network: 'Hyperledger Fabric',
      role: 'Peer',
      status: 'Online',
      lastSeen: 'May 31, 2025 10:30 AM',
      health: '97%',
    },
  ];

  const topIPFSFiles = [
    { name: 'Service_Agreement.pdf', cid: 'QmPTa1...2XU1c', size: '24.6 MB' },
    { name: 'Firmware_v2.1.bin', cid: 'QmN5dE...JmNp', size: '156.8 MB' },
    { name: 'NDA_Contract.pdf', cid: 'Qm6yP...2XJ7', size: '18.3 MB' },
    { name: 'Mobile_App_release.apk', cid: 'Qm9kSP...a7mT3', size: '92.7 MB' },
    { name: 'ISO_Certificate.pem', cid: 'QmP7L...n8cQ', size: '2.1 KB' },
  ];


  const formatNumber = (number) => number.toLocaleString();

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <header className="sticky top-0 z-40 bg-white">
        <div className="mx-auto max-w-[1600px] px-0">
          <div className="flex h-14 items-center justify-between">
            <nav className="flex gap-6 overflow-x-auto">
              {tabs.map((tab) => {
                const tabKey = tab.toLowerCase().replace(/\s+/g, '-');

                return (
                  <button
                    key={tab}
                    onClick={() => handleTabClick(tabKey)}
                    className={getTabClass(activeTab === tabKey)}
                  >
                    {tab}
                  </button>
                );
              })}
            </nav>

            <button
              onClick={openModal}
              className="flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-blue-700"
            >
              <span className="text-sm">+</span>
              Anchor New Record
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] space-y-4 px-0 pb-5">
        <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <MetricCard
            title="Records Anchored"
            value="12,458"
            change="↑ 18.6% vs last month"
            icon={Box}
            iconColor="text-blue-600"
          />

          <MetricCard
            title="IPFS Files Stored"
            value="8,732"
            change="↑ 21.4% vs last month"
            icon={Box}
            iconColor="text-teal-600"
          />

          <MetricCard
            title="Total Data Stored"
            value="2.45 TB"
            change="↑ 14.2% vs last month"
            icon={Database}
            iconColor="text-blue-600"
          />

          <MetricCard
            title="Verification Success"
            value="99.98%"
            change="↑ 0.08% vs last month"
            icon={ShieldCheck}
            iconColor="text-green-600"
          />

          <MetricCard
            title="Nodes Online"
            value="15 / 16"
            change="Healthy"
            icon={Network}
            iconColor="text-yellow-600"
            changeColor="text-green-600"
          />

          <MetricCard
            title="Smart Contracts"
            value="6"
            change="Active"
            icon={Code2}
            iconColor="text-purple-600"
            changeColor="text-green-600"
          />
        </section>

        <section className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <div className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-bold">Anchored Records Trend</h2>

              <button className="rounded border border-gray-200 px-2 py-1 text-[10px] text-gray-600">
                Last 30 Days
              </button>
            </div>

            <ResponsiveContainer width="100%" height={190}>
              <LineChart
                data={trendData}
                margin={{ top: 8, right: 8, left: -20, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e5e7eb"
                  vertical={false}
                />

                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  stroke="#9ca3af"
                  tick={{ fontSize: 10 }}
                />

                <YAxis
                  axisLine={false}
                  tickLine={false}
                  stroke="#9ca3af"
                  tick={{ fontSize: 10 }}
                />

                <Tooltip />

                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#4f46e5"
                  strokeWidth={2}
                  dot={{
                    fill: '#4f46e5',
                    stroke: '#ffffff',
                    strokeWidth: 2,
                    r: 3,
                  }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="mb-2 text-sm font-bold">Records by Type</h2>

            <div className="grid grid-cols-[140px_1fr] items-center gap-2">
              <DonutChart data={recordsTypeData} total="12,458" height={160} />

              <div className="space-y-1.5">
                {recordsTypeData.map((item, index) => (
                  <div
                    key={item.name}
                    className="flex items-center justify-between gap-1 text-[10px]"
                  >
                    <div className="flex min-w-0 items-center gap-1.5">
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{
                          backgroundColor: colors[index % colors.length],
                        }}
                      />

                      <span className="truncate text-gray-600">
                        {item.name}
                      </span>
                    </div>

                    <span className="shrink-0 font-medium text-gray-500">
                      {formatNumber(item.value)} ({item.percentage}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="mb-2 text-sm font-bold">Storage by Network</h2>

            <div className="grid grid-cols-[140px_1fr] items-center gap-2">
              <DonutChart
                data={storageNetworkData}
                total="12,458"
                height={160}
              />

              <div className="space-y-1.5">
                {storageNetworkData.map((item, index) => (
                  <div
                    key={item.name}
                    className="flex items-center justify-between gap-1 text-[10px]"
                  >
                    <div className="flex min-w-0 items-center gap-1.5">
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{
                          backgroundColor: colors[index % colors.length],
                        }}
                      />

                      <span className="truncate text-gray-600">
                        {item.name}
                      </span>
                    </div>

                    <span className="shrink-0 font-medium text-gray-500">
                      {formatNumber(item.value)} ({item.percentage}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_320px]">
          <div className="min-w-0 rounded-md border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-bold">Recent Anchored Records</h2>

              <button className="text-[11px] font-medium text-blue-600 hover:text-blue-700">
                View all records →
              </button>
            </div>

            <div className="overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-200 text-[10px] uppercase text-gray-500">
                    <th className="px-2 py-2 font-semibold">ID</th>
                    <th className="px-2 py-2 font-semibold">Type</th>
                    <th className="px-2 py-2 font-semibold">File / Reference</th>
                    <th className="px-2 py-2 font-semibold">
                      IPFS CID / TX Hash
                    </th>
                    <th className="px-2 py-2 font-semibold">
                      Blockchain / Network
                    </th>
                    <th className="px-2 py-2 font-semibold">Block / Time</th>
                    <th className="px-2 py-2 font-semibold">Status</th>
                    <th className="px-2 py-2 text-center font-semibold">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {recentRecords.map((record) => (
                    <tr
                      key={record.id}
                      onClick={() => setSelectedRecord(record)}
                      className={`cursor-pointer border-b border-gray-100 text-[10px] transition hover:bg-gray-50 ${
                        selectedRecord?.id === record.id ? 'bg-blue-50/50' : ''
                      }`}
                    >
                      <td className="whitespace-nowrap px-2 py-2 font-medium text-gray-900">
                        {record.id}
                      </td>

                      <td className="whitespace-nowrap px-2 py-2 text-gray-600">
                        {record.type}
                      </td>

                      <td className="whitespace-nowrap px-2 py-2 text-gray-600">
                        {record.identifier}
                      </td>

                      <td className="whitespace-nowrap px-2 py-2 font-mono text-blue-600">
                        {record.ipfsHash}
                      </td>

                      <td className="whitespace-nowrap px-2 py-2 text-gray-600">
                        {record.networks}
                      </td>

                      <td className="whitespace-nowrap px-2 py-2 text-gray-600">
                        {record.blocks}
                      </td>

                      <td className="px-2 py-2">
                        <span className="rounded bg-green-100 px-2 py-0.5 text-[9px] font-semibold text-green-700">
                          {record.status}
                        </span>
                      </td>

                      <td className="px-2 py-2">
                        <div className="flex items-center justify-center gap-2 text-gray-400">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRecord(record);
                            }}
                            className="transition hover:text-blue-600"
                            title="View Details"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </button>

                          <button
                            onClick={(e) => e.stopPropagation()}
                            className="transition hover:text-gray-600"
                            title="More Options"
                          >
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="h-fit rounded-md border border-gray-200 bg-white p-3.5 shadow-sm">
            <div className="mb-2.5 flex items-center justify-between border-b border-gray-100 pb-2">
              <h2 className="text-xs font-bold text-gray-900">Record Details</h2>

              <button className="flex items-center gap-1 rounded border border-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-600 hover:bg-gray-50">
                <Edit className="h-3 w-3" />
                Verify Record
              </button>
            </div>

            {selectedRecord ? (
              <div className="space-y-1.5 text-[11px]">
                <div className="grid grid-cols-2 border-b border-gray-50 py-1">
                  <span className="font-medium text-gray-500">Record ID</span>
                  <span className="font-semibold text-gray-900">
                    {selectedRecord.id}
                  </span>
                </div>

                <div className="grid grid-cols-2 border-b border-gray-50 py-1">
                  <span className="font-medium text-gray-500">Type</span>
                  <span className="text-gray-800">{selectedRecord.type}</span>
                </div>

                <div className="grid grid-cols-2 border-b border-gray-50 py-1">
                  <span className="font-medium text-gray-500">File Name</span>
                  <span className="text-gray-800">
                    {selectedRecord.identifier}
                  </span>
                </div>

                <div className="grid grid-cols-2 border-b border-gray-50 py-1">
                  <span className="font-medium text-gray-500">IPFS CID</span>
                  <span className="font-mono text-blue-600">
                    {selectedRecord.ipfsHash}
                  </span>
                </div>

                <div className="grid grid-cols-2 border-b border-gray-50 py-1">
                  <span className="font-medium text-gray-500">
                    Transaction Hash
                  </span>
                  <span className="truncate font-mono text-blue-600">
                    {selectedRecord.txHash}
                  </span>
                </div>

                <div className="grid grid-cols-2 border-b border-gray-50 py-1">
                  <span className="font-medium text-gray-500">
                    Blockchain / Network
                  </span>
                  <span className="text-gray-800">
                    {selectedRecord.networks}
                  </span>
                </div>

                <div className="grid grid-cols-2 border-b border-gray-50 py-1">
                  <span className="font-medium text-gray-500">
                    Block Number
                  </span>
                  <span className="text-gray-800">{selectedRecord.blocks}</span>
                </div>

                <div className="grid grid-cols-2 border-b border-gray-50 py-1">
                  <span className="font-medium text-gray-500">Timestamp</span>
                  <span className="text-gray-800">
                    {selectedRecord.timestamp}
                  </span>
                </div>

                <div className="grid grid-cols-2 border-b border-gray-50 py-1">
                  <span className="font-medium text-gray-500">Status</span>
                  <span className="font-semibold text-green-600">
                    {selectedRecord.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 border-b border-gray-50 py-1">
                  <span className="font-medium text-gray-500">
                    Confirmations
                  </span>
                  <span className="text-gray-800">
                    {selectedRecord.confirmations}
                  </span>
                </div>

                <div className="pt-2">
                  <button className="text-[10px] font-medium text-blue-600 hover:underline">
                    View Metadata →
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex min-h-[220px] flex-col items-center justify-center text-center text-gray-500">
                <Eye className="mb-2 h-6 w-6 text-gray-300" />
                <p className="text-xs">Select a record to view details</p>
              </div>
            )}
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <div className="min-w-0 rounded-md border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-bold">Network Nodes</h2>

              <button className="text-[11px] font-medium text-blue-600 hover:text-blue-700">
                View All Nodes
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-left">
                <thead>
                  <tr className="border-b border-gray-200 text-[10px] uppercase text-gray-500">
                    <th className="px-2 py-2 font-semibold">Node ID</th>
                    <th className="px-2 py-2 font-semibold">Network</th>
                    <th className="px-2 py-2 font-semibold">Role</th>
                    <th className="px-2 py-2 font-semibold">Status</th>
                    <th className="px-2 py-2 font-semibold">Last Seen</th>
                    <th className="px-2 py-2 font-semibold">Health</th>
                  </tr>
                </thead>

                <tbody>
                  {networkNodes.map((node) => (
                    <tr
                      key={node.nodeId}
                      className="border-b border-gray-100 text-[10px] hover:bg-gray-50"
                    >
                      <td className="whitespace-nowrap px-2 py-2 font-medium text-gray-900">
                        {node.nodeId}
                      </td>

                      <td className="whitespace-nowrap px-2 py-2 text-gray-600">
                        {node.network}
                      </td>

                      <td className="whitespace-nowrap px-2 py-2 text-gray-600">
                        {node.role}
                      </td>

                      <td className="px-2 py-2">
                        <span className="rounded bg-green-100 px-2 py-1 text-[9px] font-semibold text-green-700">
                          {node.status}
                        </span>
                      </td>

                      <td className="whitespace-nowrap px-2 py-2 text-gray-600">
                        {node.lastSeen}
                      </td>

                      <td className="px-2 py-2 text-gray-600">
                        <div className="flex items-center gap-2">
                          <span>{node.health}</span>

                          <span className="h-1.5 w-12 overflow-hidden rounded-full bg-gray-200">
                            <span
                              className="block h-full rounded-full bg-green-500"
                              style={{ width: node.health }}
                            />
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="mb-2 text-sm font-bold">IPFS Storage Overview</h2>

            <div className="grid grid-cols-[150px_1fr] items-center gap-3">
              <DonutChart
                data={ipfsStorageData}
                total="2.45 TB"
                totalLabel="Total"
                height={180}
              />

              <div className="space-y-3">
                {ipfsStorageData.map((item, index) => (
                  <div
                    key={item.name}
                    className="flex items-center justify-between gap-3 text-[10px]"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{
                          backgroundColor: colors[index % colors.length],
                        }}
                      />

                      <span className="text-gray-600">{item.name}</span>
                    </div>

                    <span className="font-medium text-gray-500">
                      {item.value.toFixed(2)} TB ({item.percentage}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-bold">Top IPFS Files</h2>

              <button className="text-[11px] font-medium text-blue-600">
                View All
              </button>
            </div>

            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-200 text-[10px] uppercase text-gray-500">
                  <th className="px-2 py-1.5 font-semibold">File Name</th>
                  <th className="px-2 py-1.5 font-semibold">CID</th>
                  <th className="px-2 py-1.5 font-semibold">Size</th>
                </tr>
              </thead>

              <tbody>
                {topIPFSFiles.map((file) => (
                  <tr
                    key={file.name}
                    className="border-b border-gray-100 text-[10px] hover:bg-gray-50"
                  >
                    <td className="px-2 py-1.5 font-medium text-gray-900">
                      {file.name}
                    </td>

                    <td className="px-2 py-1.5 font-mono text-blue-600">
                      {file.cid}
                    </td>

                    <td className="px-2 py-1.5 text-gray-600">{file.size}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {showAnchorModal && (
        <div
          className="fixed z-[200] flex items-center justify-center bg-black/40"
          style={{
            top: 0,
            right: 0,
            bottom: 0,
            left: getSidebarWidth(),
          }}
          onClick={closeModal}
        >
          <div
            className={getModalClass(modalStep)}
            style={{}}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
                  <ShieldCheck className="h-5 w-5" />
                </div>

                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    {MODAL_TITLES[modalStep]}
                  </h2>

                  <p className="text-xs text-gray-500">
                    {MODAL_SUBTITLES[modalStep]}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {modalStep === 3 && (
                  <button
                    onClick={openModal}
                    className="flex items-center gap-1.5 rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-1.5 text-xs font-semibold text-indigo-600 transition hover:bg-indigo-100"
                  >
                    <span className="text-sm">+</span> Anchor New Record
                  </button>
                )}
                <button
                  onClick={closeModal}
                  className="text-2xl leading-none text-gray-400 transition hover:text-gray-700"
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Stepper Header */}
            <div className="flex items-center px-6 py-3 border-b border-gray-100 bg-gray-50/50">
              <StepBadge modalStep={modalStep} step={1} label="Record Details" />
              <div className="mx-4 h-px flex-1 bg-gray-200" />
              <StepBadge modalStep={modalStep} step={2} label="Security & Signing" />
              <div className="mx-4 h-px flex-1 bg-gray-200" />
              <StepBadge modalStep={modalStep} step={3} label="Review & Anchor" />
            </div>

            {/* ---------- STEP 1 ---------- */}
            {modalStep === 1 && (
              <AnchorStep1 closeModal={closeModal} setModalStep={setModalStep} />
            )}

            {/* ---------- STEP 2 ---------- */}
            {modalStep === 2 && (
              <AnchorStep2
                form={form}
                setModalStep={setModalStep}
              />
            )}

            {/* ---------- STEP 3 (REVIEW & ANCHOR FULL PAGE UI) ---------- */}
            {modalStep === 3 && (
              <AnchorStep3
                signingAlgo={signingAlgo}
                keySource={keySource}
                certificate={certificate}
                timestampAuth={timestampAuth}
                timestampAlgo={timestampAlgo}
                includeBlockchain={includeBlockchain}
                confirmCheck={confirmCheck}
                setConfirmCheck={setConfirmCheck}
                setModalStep={setModalStep}
                closeModal={closeModal}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}